import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table'
import { CHAIN_COLUMNS } from 'components/DataExpansionComponents/Chains/chainColumnsInfo'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { getCommonChainCRMReportColumns } from 'components/DataExpansionComponents/Common/Reports/ChainCRM/ChainCRMReportColumns'
import { DataTableColumnSort } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import { OverflownText } from 'components/OverflownText'
import { TruncatedLinkCell } from '../CommonTableCells/TruncatedLinkCell'
import { OverflownScrollList } from '../CommonTableCells/OverflownScrollList'
import { PredictedTrafficBadge } from 'components/PredictedTrafficBadge/PredictedTrafficBadge'
import { ExpenseCategoryBadge } from 'components/ExpenseCategoryBadge/ExpenseCategoryBadge'
import { Anchor } from 'components/UI/Anchor'
import { ChainProxyReport } from 'models/chain_reports'
import { ContactCompanyType } from 'models/contact_companies'
import { menu_ingredients } from 'models/menu_ingredients'
import { Preferences } from 'models/preferences'
import { ChainsSummary } from 'models/summaries'
import { useMemo } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { PiForkKnife } from 'react-icons/pi'
import { MenuIcon } from 'components/MenuIcon/MenuIcon'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { formatInteger, formatPercentage, formatUsd } from 'utils/formatting'
import { getPathFromUrl } from 'utils/getPathFromUrl'
import { CHAIN_CRM_REPORT_COLUMNS } from 'components/DataExpansionComponents/Common/Reports/ChainCRM/ChainCRMReportColumnsInfo'
import { commonSortOptionsHelper } from 'components/DataExpansionComponents/Common/commonSortOptionsHelper'

export function useChainsTableColumnsV2(
  chainsSummary: ChainsSummary | undefined,
  setMenuModal: any,
  sorting: DataTableColumnSort[] | undefined,
  data: any,
  selectedProducts: number[],
  preferences: Preferences | undefined,
  inCampaigns?: boolean
): ColumnDef<ChainProxyReport>[] {
  const featureFlag = useFeatureFlagService()
  const tooltips: Record<string, any> = preferences?.tooltips || {}
  const columnHelper = createColumnHelper<ChainProxyReport>()
  const chainColumnHelper = createTableColumnHelper({
    columns: CHAIN_COLUMNS,
    tooltips,
  })

  const defaultCell = (info: CellContext<ChainProxyReport, any>) => {
    const value = info.getValue()
    if (!value) return '-'
    return value
  }

  const sortedArrayCell = (
    info: CellContext<ChainProxyReport, string[] | undefined>,
    field: string
  ) => {
    let value = info.getValue()
    if (sorting?.length && sorting[0].id === field) {
      const sortAsc = !sorting[0].desc
      value = value?.sort((a, b) =>
        sortAsc ? a.localeCompare(b) : b.localeCompare(a)
      )
    }
    if (!value || value.length === 0) return '-'
    // OverflownScrollList opens a click-triggered popover on DS2026 surfaces
    // (variant="plain") and falls back to the legacy truncated/tooltip render
    // elsewhere — see component implementation for details.
    return <OverflownScrollList items={value} />
  }

  const columns = useMemo(() => {
    const commonColumns = getCommonChainCRMReportColumns(
      columnHelper as any,
      sorting,
      preferences
    )

    let cols = [
      columnHelper.accessor('chain.chain', {
        ...chainColumnHelper.retrieveColumn('chain', {
          meta: {
            isEditable: false,
            isFirstColumn: true,
          },
        }),
        header: () => null,
        size: 332,
        cell: (info) => {
          if (info.row.original.chain?.id) {
            return (
              <TruncatedLinkCell
                text={info.getValue() || ''}
                to={`/chain/${preferences?.company_type_prefix_map[ContactCompanyType.Chain]}${info.row.original.chain.id}`}
              />
            )
          } else {
            return (
              <TruncatedLinkCell
                text={info.row.original.overriden_name || ''}
                to={`/company/${preferences?.company_type_prefix_map[ContactCompanyType.UserGenerated]}${info.row.original.contact_company_id}`}
              />
            )
          }
        },
      }),
      columnHelper.accessor('chain.count', {
        ...chainColumnHelper.retrieveColumn('count', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 110,
        cell: (info) => {
          return info.getValue() ? formatInteger(info.getValue()) : '1'
        },
      }),
      columnHelper.display({
        ...chainColumnHelper.retrieveColumn('menu'),
        size: 100,
        cell: (info) => {
          const items = info.row.original.search_items?.map(
            (item) => item.item_id
          )
          const menuLength = info.row.original.chain?.menu_length

          if (!menuLength)
            return (
              <div className="ml-1">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Unavailable</Tooltip>}
                >
                  <div className="max-w-max">
                    <PiForkKnife size={20} className="text-gray-400" />
                  </div>
                </OverlayTrigger>
              </div>
            )

          return (
            <div
              className="ml-1 cursor-pointer"
              onClick={() =>
                setMenuModal({
                  chainId: info.row.original.chain?.id,
                  chainName: info.row.original.chain?.chain || '',
                  itemsIds: items,
                  menuLength,
                })
              }
            >
              <MenuIcon size={20} />
            </div>
          )
        },
      }),
      columnHelper.accessor('chain.menu_length', {
        ...chainColumnHelper.retrieveColumn('menu_length'),
        size: 100,
        cell: (info) => {
          return info.getValue() ? formatInteger(info.getValue()) : '-'
        },
      }),
      columnHelper.accessor('menu_matches', {
        ...chainColumnHelper.retrieveColumn('menu_matches', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 110,
        cell: (info) => {
          return info.getValue() ? formatInteger(info.getValue()) : '-'
        },
      }),
      columnHelper.accessor('total_ltv', {
        ...chainColumnHelper.retrieveColumn('total_ltv', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 200,
        header: () => {
          return chainsSummary?.avg_total_ltv ? (
            <>
              <div>LTV</div>
              <span className="text-xs">
                Avg: {formatUsd(chainsSummary?.avg_total_ltv)}
              </span>
            </>
          ) : (
            <span>LTV</span>
          )
        },
        cell: (info) => {
          const value = info.getValue()
          return value ? formatUsd(value) : '-'
        },
      }),
      columnHelper.accessor('brand_ltv', {
        ...chainColumnHelper.retrieveColumn('brand_ltv', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          return value ? formatUsd(value) : '-'
        },
      }),
      columnHelper.accessor('revenue_ltv', {
        ...chainColumnHelper.retrieveColumn('revenue_ltv', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          return value ? formatUsd(value) : '-'
        },
      }),
      columnHelper.accessor('one_year_total_value', {
        ...chainColumnHelper.retrieveColumn('one_year_total_value', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          return value ? formatUsd(value) : '-'
        },
      }),
      columnHelper.accessor('taro', {
        ...chainColumnHelper.retrieveColumn('taro', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 180,
        cell: (info) => {
          const value = info.getValue()
          return value ? formatUsd(value) : '-'
        },
      }),
      columnHelper.accessor('tabo', {
        ...chainColumnHelper.retrieveColumn('tabo', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          return value ? formatUsd(value) : '-'
        },
      }),
      columnHelper.accessor('pounds_per_year', {
        ...chainColumnHelper.retrieveColumn('pounds_per_year', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 150,
        header: () => {
          return chainsSummary?.avg_pounds_per_year ? (
            <>
              <div style={{ marginTop: '12px' }}>1yr Volume</div>
              <span className={'text-xs'}>
                Avg: {formatInteger(chainsSummary?.avg_pounds_per_year)}
              </span>
            </>
          ) : (
            <span>1yr Volume</span>
          )
        },
        cell: (info) =>
          info.getValue() ? formatInteger(info.getValue()) : '-',
      }),
      columnHelper.accessor('chain.domain', {
        ...chainColumnHelper.retrieveColumn('domain'),
        size: 240,
        cell: (info) => {
          const val = info.getValue()
          return val ? (
            <Anchor href={'//' + val} target="_blank">
              {val}
            </Anchor>
          ) : (
            '-'
          )
        },
      }),
      columnHelper.accessor('chain.cuisine_50', {
        ...chainColumnHelper.retrieveColumn('cuisine_50'),
        size: 150,
        cell: defaultCell,
      }),
      columnHelper.accessor('chain.velocity_group', {
        ...chainColumnHelper.retrieveColumn('velocity_group'),
        size: 200,
        cell: (info) => (
          <PredictedTrafficBadge value={info.getValue() ?? null} />
        ),
      }),
      columnHelper.accessor('chain.rating', {
        ...chainColumnHelper.retrieveColumn('rating'),
        size: 70,
        cell: (info) => (info.getValue() ? info.getValue()?.toFixed(2) : '-'),
      }),
      columnHelper.accessor('chain.reviews_count', {
        ...chainColumnHelper.retrieveColumn('reviews_count', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 150,
        cell: (ctx) => (ctx.getValue() ? formatInteger(ctx.getValue()) : '-'),
      }),
      columnHelper.accessor('chain.expense_category', {
        ...chainColumnHelper.retrieveColumn('expense_category'),
        size: 100,
        cell: (info) => (
          <ExpenseCategoryBadge value={info.getValue() ?? null} />
        ),
      }),
      columnHelper.accessor('chain.ingredients_matrix', {
        ...chainColumnHelper.retrieveColumn('ingredients_matrix'),
        size: 300,
        cell: (info) => {
          const ingredients = info.row.original.chain?.ingredients_matrix
          const display: string[] = []

          if (!ingredients) return null

          const ingredientsValuesWhitelist = menu_ingredients.map(
            (item) => item.value
          )

          Object.entries(ingredients)
            .filter(([key]) => ingredientsValuesWhitelist.includes(key))
            .map(([key, value]) => {
              if (Number(value) > 0) display.push(key)
            })

          return (
            <OverflownText maxLines={2}>{display.join(', ')}</OverflownText>
          )
        },
      }),
      columnHelper.accessor('chain.instagram.url', {
        ...chainColumnHelper.retrieveColumn('instagram_url'),
        size: 220,
        cell: (info) => {
          const value = info.row.original.chain?.instagram?.url
          if (!value) return '-'

          return (
            <Anchor href={value} target="_blank">
              @{getPathFromUrl(value)}
            </Anchor>
          )
        },
      }),
      columnHelper.accessor('chain.instagram.followers', {
        ...chainColumnHelper.retrieveColumn('instagram_followers', {
          meta: {
            rightAlign: true,
          },
        }),
        cell: (info) => {
          const value = info.row.original.chain?.instagram?.followers
          if (!value) return '-'
          return formatInteger(value)
        },
      }),
      columnHelper.accessor('chain.instagram.following', {
        ...chainColumnHelper.retrieveColumn('instagram_following', {
          meta: {
            rightAlign: true,
          },
        }),
        cell: (info) => {
          const value = info.row.original.chain?.instagram?.following
          if (!value) return '-'
          return formatInteger(value)
        },
      }),
      columnHelper.accessor('chain.instagram.posts', {
        ...chainColumnHelper.retrieveColumn('instagram_posts', {
          meta: {
            rightAlign: true,
          },
        }),
        cell: (info) => {
          const value = info.row.original.chain?.instagram?.posts
          if (!value) return '-'
          return formatInteger(value)
        },
      }),
      columnHelper.accessor('chain.instagram.text_percentile', {
        ...chainColumnHelper.retrieveColumn('instagram_text_percentile'),
        size: 200,
        cell: (info) => (
          <PredictedTrafficBadge value={info.getValue() ?? null} />
        ),
      }),
      ...commonColumns,
      columnHelper.accessor('chain.country_summary', {
        ...chainColumnHelper.retrieveColumn('country_summary'),
        size: 120,
        cell: (info) => {
          const countries = info.getValue()
          if (!countries) return '-'

          const countriesSorted = countries?.split(', ').sort()
          const display = countriesSorted?.slice(0, 25).join(', ')
          const totalCountries = countriesSorted?.length
          return (
            <OverflownText
              tooltipText={`${display}, ... (${totalCountries} more)`}
              maxLines={2}
            >
              {display}
            </OverflownText>
          )
        },
      }),
      columnHelper.accessor('chain.state_summary', {
        ...chainColumnHelper.retrieveColumn('state_summary'),
        size: 300,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return '-'
          return <OverflownScrollList items={value.split(', ')} />
        },
      }),
      columnHelper.accessor('chain.city_summary', {
        ...chainColumnHelper.retrieveColumn('city_summary'),
        size: 300,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return '-'
          return <OverflownScrollList items={value.split(', ').sort()} />
        },
      }),
      columnHelper.accessor('chain.dma_summary', {
        ...chainColumnHelper.retrieveColumn('dma_summary'),
        size: 300,
        cell: (info) => {
          const dmaz = info.getValue()
          if (!dmaz) return '-'
          // Split into "City, ST" pairs (raw value is comma-separated tokens).
          const dmazArray: string[] = []
          const dmazSplit = dmaz.split(', ')
          for (let i = 0; i < dmazSplit.length; i += 2) {
            dmazArray.push(`${dmazSplit[i]}, ${dmazSplit[i + 1]}`)
          }
          return <OverflownScrollList items={dmazArray.sort()} separator="; " />
        },
      }),
      columnHelper.accessor('chain.zip_summary', {
        ...chainColumnHelper.retrieveColumn('zip_summary'),
        size: 300,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return '-'
          return <OverflownScrollList items={value.split(', ').sort()} />
        },
      }),
      columnHelper.accessor('chain.median_hhi', {
        ...chainColumnHelper.retrieveColumn('median_hhi'),
        size: 200,
        cell: (info) => formatUsd(info.getValue()),
      }),
      columnHelper.accessor('chain.pop_density', {
        ...chainColumnHelper.retrieveColumn('pop_density'),
        cell: (info) => formatInteger(info.getValue() ?? 0) + '/mi²',
      }),
      columnHelper.accessor('chain.hh_gt100k', {
        ...chainColumnHelper.retrieveColumn('hh_gt100k'),
        cell: (info) => {
          const value = info.getValue() ? info.getValue()! * 100 : 0
          return formatPercentage(value) + '%'
        },
      }),
      columnHelper.display({
        ...chainColumnHelper.retrieveColumn('reputation_data'),
        size: 200,
        cell: (info) => {
          const { michelin_ids_count, eater_ids_count, infatuation_ids_count } =
            info.row?.original?.chain || {}
          const reputationPlatforms = [
            michelin_ids_count && 'Michelin',
            eater_ids_count && 'Eater',
            infatuation_ids_count && 'Infatuation',
          ].filter(Boolean)

          if (reputationPlatforms.length === 0) return '-'
          return (
            <div className="flex flex-wrap gap-1">
              {reputationPlatforms.map((platform, index) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded-2xl bg-neutral-100 px-2.5 py-0.5 text-sm font-medium leading-5 text-neutral-700"
                  style={{ letterSpacing: '-0.2px' }}
                >
                  {platform}
                </span>
              ))}
            </div>
          )
        },
      }),
      columnHelper.accessor('chain.michelin_stars_count', {
        ...chainColumnHelper.retrieveColumn('michelin_stars_count'),
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          if (value === undefined || value === null) return '-'

          const { michelin_ids_count } = info.row?.original?.chain || {}
          if (!michelin_ids_count) return '-'
          return value
        },
      }),
      columnHelper.accessor('chain.services', {
        ...chainColumnHelper.retrieveColumn('services'),
        cell: (info) => sortedArrayCell(info, 'services'),
      }),
      columnHelper.accessor('chain.atmospheres', {
        ...chainColumnHelper.retrieveColumn('atmospheres'),
        cell: (info) => sortedArrayCell(info, 'atmospheres'),
      }),
      columnHelper.accessor('chain.offerings', {
        ...chainColumnHelper.retrieveColumn('offerings'),
        cell: (info) => sortedArrayCell(info, 'offerings'),
      }),
      columnHelper.accessor('chain.open_hours', {
        ...chainColumnHelper.retrieveColumn('open_hours'),
        cell: (info) => sortedArrayCell(info, 'open_hours'),
      }),
      columnHelper.accessor('chain.hq_state1', {
        ...chainColumnHelper.retrieveColumn('hq_state1'),
        size: 100,
        cell: (info) => {
          const value = info.getValue()
          return value || '-'
        },
      }),
      columnHelper.accessor('chain.hq_city1', {
        ...chainColumnHelper.retrieveColumn('hq_city1'),
        cell: (info) => {
          const value = info.getValue()
          return value || '-'
        },
      }),
      columnHelper.accessor('chain.hq_state2', {
        ...chainColumnHelper.retrieveColumn('hq_state2'),
        size: 100,
        cell: (info) => {
          const value = info.getValue()
          return value || '-'
        },
      }),
      columnHelper.accessor('chain.hq_city2', {
        ...chainColumnHelper.retrieveColumn('hq_city2'),
        cell: (info) => {
          const value = info.getValue()
          return value || '-'
        },
      }),
    ]

    if (!featureFlag.enableHQStateColumn) {
      cols = cols.filter(
        (col) =>
          col.id !== 'hq_state1' &&
          col.id !== 'hq_state2' &&
          col.id !== 'hq_city1' &&
          col.id !== 'hq_city2'
      )
    }

    if (!featureFlag.enableOppsAttrsServicesAtmosOffersHours) {
      cols = cols.filter(
        (col) =>
          !['services', 'atmospheres', 'offerings', 'open_hours'].includes(
            col.id ?? ''
          )
      )
    }

    if (!featureFlag.enableCampaigns) {
      cols = cols.filter((col) => !col.id?.includes('campaigns'))
    }

    if (!featureFlag.enableInstagremData) {
      cols = cols.filter((col) => !col.id?.includes('instagram'))
    }

    if (!featureFlag.enableInstagramPercentile) {
      cols = cols.filter((col) => col.id !== 'instagram_text_percentile')
    }

    if (!featureFlag.shouldShowMenu) {
      cols = cols.filter((col) => col.id !== 'menu')
    }

    if (!featureFlag.shouldShowMenuSize) {
      cols = cols.filter((col) => col.id !== 'menu_length')
    }

    if (featureFlag.hideIngredientsMatrix) {
      cols = cols.filter((col) => col.id !== 'ingredients_matrix')
    }

    if (!featureFlag.shouldShowReputationData) {
      cols = cols.filter(
        (col) =>
          !['reputation_data', 'michelin_stars_count'].includes(col?.id ?? '')
      )
    }

    if (inCampaigns) {
      cols.push(
        columnHelper.accessor('campaign_attributed_deal_count', {
          ...chainColumnHelper.retrieveColumn(
            'campaign_attributed_deal_count',
            {
              meta: {
                rightAlign: true,
              },
            }
          ),
          size: 110,
          cell: (info) => {
            return info.row.original.campaign_attributed_deal_count || '-'
          },
        })
      )
    }

    return cols
  }, [
    data,
    selectedProducts,
    sorting,
    preferences,
    inCampaigns,
    featureFlag.enableInstagremData,
    featureFlag.shouldShowMenu,
    featureFlag.shouldShowMenuSize,
    featureFlag.shouldShowReputationData,
    featureFlag.hideIngredientsMatrix,
  ])

  return columns as ColumnDef<ChainProxyReport>[]
}

// Fields that support server-side sorting
// Note: The following fields are NOT sortable because they're computed in the SQL SELECT clause:
// - services, atmospheres, offerings, open_hours (built from array_remove(ARRAY[...]))
// These fields will only support client-side sorting of the current page
export const sortableFields = [
  'relevancy',
  'chain',
  'count',
  'total_ltv',
  'brand_ltv',
  'revenue_ltv',
  'pounds_per_year',
  'domain',
  'cuisine_50',
  'expense_category',
  'reviews_count',
  'rating',
  'velocity_group',
  'campaign_attributed_deal_count',
  'deal_count',
  'contact_count',
  'taro',
  'tabo',
  'one_year_total_value',
  'distributors',
  'menu_length',
  'menu_matches',
  'michelin_stars_count',
  'sales_stages',
  'country_summary',
  'state_summary',
  'city_summary',
  'dma_summary',
  'zip_summary',
  'state_summary_length',
  'instagram_text_percentile',
  'instagram_followers',
  'instagram_following',
  'instagram_posts',
  'hq_state1',
  'hq_city1',
  'hq_state2',
  'hq_city2',
  'median_hhi',
  'pop_density',
  'hh_gt100k',
  'note_count',
  'account_owners',
  'gpos',
  'campaigns',
  'taglist',
] as const

export const sortableFieldOptions = commonSortOptionsHelper(
  sortableFields,
  [CHAIN_COLUMNS, CHAIN_CRM_REPORT_COLUMNS],
  {
    relevancy: 'Relevancy',
    state_summary_length: '# of States',
    chain: (_, direction) => `Name (${direction === 'asc' ? 'A-Z' : 'Z-A'})`,
  }
)
